import { HttpError } from "react-admin";

import { createApiClient } from "@/api";
import { HTTP_STATUS } from "@/constants/http";
import { MODULE_NAMES } from "@/constants/modules";
import { type DataProviderSchema, type GetEntityTypes } from "@/utils";
import { getFullListQueryData } from "@/utils/query";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/main/groups";

const fetchListData = async () => {
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: false,
      },
    },
  });

  const data = response.data?.["hydra:member"] ?? [];

  return {
    data,
    total: response.data?.["hydra:totalItems"] ?? 0,
  };
};

export const groupsSchema: DataProviderSchema<"Group", "jsonld"> = {
  list: fetchListData,
  many: async () => {
    const queryData = getFullListQueryData<{
      data: GetEntityTypes<"Group">["read"][];
    }>(MODULE_NAMES.GROUPS);

    if (queryData) {
      return {
        data: queryData.data,
      };
    }

    const fetchedData = await fetchListData();

    return {
      data: fetchedData.data,
    };
  },
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("Group not found");

    return { data };
  },
  delete: (async (entity: { id: string }) =>
    await apiClient.DELETE(`${BASE_URL}/{id}`, {
      params: {
        path: { id: entity.id },
      },
    })) as unknown as NonNullable<
    DataProviderSchema<"Group", "jsonld">["delete"]
  >,
  create: async ({ data: { name = "", description = "", roles } }) => {
    const { data } = await apiClient.POST(BASE_URL, {
      body: {
        name,
        description,
        roles,
      },
    });

    if (!data)
      throw new HttpError(
        "Something went wrong!",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );

    return {
      data,
    };
  },
  edit: async ({ id, data: { name = "", description = "", roles = [] } }) => {
    const { data } = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        name,
        description,
        roles,
      },
    });

    if (!data) throw Error("Something went wrong");

    return { data };
  },
};
